import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Zap, Eye, Upload, Star, Plus, Music } from 'lucide-react';

const GettingStarted = () => {
  return (
    <>
      <Helmet>
        <title>Getting Started - Light Hunt</title>
        <meta name="description" content="Welcome to Light Hunt! Learn how to browse displays, set up your own display page, upload sequences, and join the growing community of light show creators and fans." />
        <meta name="keywords" content="getting started, light display setup, sequence upload, holiday lights guide, light hunt tutorial" />
      </Helmet>

      <div className="min-h-screen flex flex-col">
        <NavBar />
        <main className="grow container mx-auto px-4 py-8 max-w-4xl">
          <div className="space-y-16">
            {/* Hero Section */}
            <div className="text-center space-y-6">
              <h1 className="text-4xl md:text-5xl font-bold bg-linear-to-r from-primary to-primary/60 bg-clip-text text-transparent">
                Getting Started with Light Hunt
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Welcome to the home for display creators and light show fans.
              </p>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                Whether you're syncing your first song or sharing a full neighborhood spectacle, Light Hunt makes it easy to discover, build, and showcase unforgettable light displays. Here's how to jump in:
              </p>
            </div>

            {/* Divider */}
            <div className="flex justify-center">
              <div className="w-16 h-px bg-linear-to-r from-transparent via-border to-transparent"></div>
            </div>

            {/* Quick Start Guide */}
            <section className="space-y-8">
              <div className="text-center">
                <div className="flex items-center justify-center space-x-2 mb-4">
                  <Zap className="h-8 w-8 text-primary" />
                  <h2 className="text-3xl font-bold">Quick Start Guide</h2>
                </div>
              </div>
              
              <div className="grid gap-8 md:gap-12">
                {/* Step 1 */}
                <div className="flex flex-col md:flex-row items-start space-y-4 md:space-y-0 md:space-x-6">
                  <div className="shrink-0 w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <span className="text-xl font-bold text-primary">1</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-2 flex items-center space-x-2">
                      <Eye className="h-5 w-5 text-primary" />
                      <span>Browse Displays & Sequences</span>
                    </h3>
                    <p className="text-muted-foreground">
                      Explore synced light shows and sequences uploaded by creators around the world.
                    </p>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="flex flex-col md:flex-row items-start space-y-4 md:space-y-0 md:space-x-6">
                  <div className="shrink-0 w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <span className="text-xl font-bold text-primary">2</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-2 flex items-center space-x-2">
                      <Plus className="h-5 w-5 text-primary" />
                      <span>Set Up Your Display</span>
                    </h3>
                    <p className="text-muted-foreground">
                      Add your display page so others can find, follow, and rate your holiday setup.
                    </p>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="flex flex-col md:flex-row items-start space-y-4 md:space-y-0 md:space-x-6">
                  <div className="shrink-0 w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <span className="text-xl font-bold text-primary">3</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-2 flex items-center space-x-2">
                      <Upload className="h-5 w-5 text-primary" />
                      <span>Upload Your Own Sequences</span>
                    </h3>
                    <p className="text-muted-foreground">
                      Share your work and help the community grow. Upload a sequence with just a few clicks.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Divider */}
            <div className="flex justify-center">
              <div className="w-16 h-px bg-linear-to-r from-transparent via-border to-transparent"></div>
            </div>

            {/* Coming Soon */}
            <section className="text-center space-y-6 bg-muted/30 rounded-2xl p-8 md:p-12">
              <h2 className="text-3xl font-bold flex items-center justify-center space-x-2">
                <span>🎥</span>
                <span>Coming Soon</span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                We're working on video walkthroughs and step-by-step screenshots to guide you through every feature — from adding songs to fine-tuning your display page.
              </p>
            </section>

            {/* Divider */}
            <div className="flex justify-center">
              <div className="w-16 h-px bg-linear-to-r from-transparent via-border to-transparent"></div>
            </div>

            {/* Key Features */}
            <section className="space-y-8">
              <div className="text-center">
                <h2 className="text-3xl font-bold flex items-center justify-center space-x-2 mb-4">
                  <span>🌟</span>
                  <span>Key Features at a Glance</span>
                </h2>
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                {/* Add Your Display */}
                <div className="p-6 rounded-lg border bg-card hover:shadow-md transition-shadow">
                  <div className="flex items-center space-x-3 mb-3">
                    <Plus className="h-6 w-6 text-primary" />
                    <h3 className="text-xl font-semibold">Add Your Display</h3>
                  </div>
                  <p className="text-muted-foreground">
                    Claim your spot and let others know where to see your synced shows.
                  </p>
                </div>

                {/* Add Songs to Display */}
                <div className="p-6 rounded-lg border bg-card hover:shadow-md transition-shadow">
                  <div className="flex items-center space-x-3 mb-3">
                    <Music className="h-6 w-6 text-primary" />
                    <h3 className="text-xl font-semibold">Add Songs to Display</h3>
                  </div>
                  <p className="text-muted-foreground">
                    Curate your 2025 playlist so fans know what to expect (and when to tune in).
                  </p>
                </div>

                {/* Upload Sequences */}
                <div className="p-6 rounded-lg border bg-card hover:shadow-md transition-shadow">
                  <div className="flex items-center space-x-3 mb-3">
                    <Upload className="h-6 w-6 text-primary" />
                    <h3 className="text-xl font-semibold">Upload Sequences</h3>
                  </div>
                  <p className="text-muted-foreground">
                    Share your version of a song — or compare interpretations side-by-side.
                  </p>
                </div>

                {/* Rate & Comment */}
                <div className="p-6 rounded-lg border bg-card hover:shadow-md transition-shadow">
                  <div className="flex items-center space-x-3 mb-3">
                    <Star className="h-6 w-6 text-primary" />
                    <h3 className="text-xl font-semibold">Rate & Comment</h3>
                  </div>
                  <p className="text-muted-foreground">
                    Support fellow creators with feedback, or discover what's trending.
                  </p>
                </div>
              </div>
            </section>

            {/* Divider */}
            <div className="flex justify-center">
              <div className="w-16 h-px bg-linear-to-r from-transparent via-border to-transparent"></div>
            </div>

            {/* Call to Action */}
            <section className="text-center space-y-6 bg-primary/5 rounded-2xl p-8 md:p-12 border border-primary/10">
              <h2 className="text-3xl font-bold flex items-center justify-center space-x-2">
                <span>🎄</span>
                <span>Ready to Dive In?</span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Let's light up the season — together.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-8">
                <Button asChild size="lg" className="px-8">
                  <Link to="/displays">
                    👉 Start Exploring
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="px-8">
                  <Link to="/upload-sequence">
                    Upload Your First Sequence
                  </Link>
                </Button>
              </div>
              <p className="text-sm text-muted-foreground mt-6">
                Join the growing Light Hunt community.
              </p>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default GettingStarted; 